import { appFetch } from '@/shared/api/app-client'
import type { ApiResponse } from '@/shared/api/app-types'

// ---------------------------------------------------------------------------
// 端点解析
// ---------------------------------------------------------------------------

const viteEnv =
  typeof import.meta !== 'undefined' && typeof import.meta.env !== 'undefined'
    ? import.meta.env
    : ({} as Record<string, string | undefined>)

function resolveLocalMainSiteUrl() {
  if (typeof window === 'undefined') return ''
  const { hostname, protocol } = window.location
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:4010`
  }
  return window.location.origin
}

const apiBaseUrl = (viteEnv.VITE_AUTH_API_BASE_URL ?? viteEnv.VITE_MAIN_SITE_URL ?? resolveLocalMainSiteUrl()).replace(/\/+$/, '')

const GENERATE_ENDPOINT = apiBaseUrl ? `${apiBaseUrl}/api/subsite/digital-human/generate` : '/api/subsite/digital-human/generate'
const DETECT_ENDPOINT = apiBaseUrl ? `${apiBaseUrl}/api/subsite/digital-human/detect` : '/api/subsite/digital-human/detect'

// ---------------------------------------------------------------------------
// 类型
// ---------------------------------------------------------------------------

export type DigitalHumanResolution = '480P' | '720P'

export type DetectResult = {
  checkPass: boolean
  humanoid: boolean
}

export const RESOLUTION_OPTIONS: ReadonlyArray<{ value: DigitalHumanResolution; label: string; pricePerSec: number }> = [
  { value: '480P', label: '480P', pricePerSec: 0.5 },
  { value: '720P', label: '720P', pricePerSec: 0.9 },
] as const

/** 演示视频配置 */
export const DEMO_VIDEOS: ReadonlyArray<{ src: string; title: string; description: string }> = [
  { src: '/video/jingqu.mp4', title: '景区', description: '景区介绍数字人演示' },
  { src: '/video/maihuo.mp4', title: '卖货', description: '直播带货数字人演示' },
  { src: '/video/yiyuan.mp4', title: '医院', description: '医院导诊数字人演示' },
] as const

// ---------------------------------------------------------------------------
// 工具函数
// ---------------------------------------------------------------------------

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('文件读取失败。'))
        return
      }
      const base64 = result.includes(',') ? (result.split(',')[1] ?? result) : result
      resolve(base64)
    }
    reader.onerror = () => reject(new Error('文件读取失败。'))
    reader.readAsDataURL(file)
  })
}

function createOutputFileName() {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `ai-digital-human-${timestamp}.mp4`
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class AliyunDigitalHumanService {
  /** 图像检测（可选前置校验） */
  async detectImage({ imageFile }: { imageFile: File }): Promise<DetectResult> {
    const imageBase64 = await fileToBase64(imageFile)
    const imageMimeType = (imageFile.type || 'image/jpeg') as 'image/jpeg' | 'image/png' | 'image/webp' | 'image/bmp'

    const res = await appFetch<ApiResponse<DetectResult>>(DETECT_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({ imageBase64, imageMimeType }),
    })
    return res.data
  }

  /** 生成数字人视频 */
  async generateVideo({
    imageFile,
    audioFile,
    resolution,
    onProgress,
  }: {
    imageFile: File
    audioFile: File
    resolution: DigitalHumanResolution
    onProgress?: (stage: string) => void
  }): Promise<{ blob: Blob; file: File; objectUrl: string }> {
    onProgress?.('正在上传图片和音频...')

    const imageBase64 = await fileToBase64(imageFile)
    const imageMimeType = (imageFile.type || 'image/jpeg') as 'image/jpeg' | 'image/png' | 'image/webp' | 'image/bmp'
    const audioBase64 = await fileToBase64(audioFile)
    const audioMimeType = (audioFile.type === 'audio/mpeg' || audioFile.type === 'audio/mp3'
      ? 'audio/mpeg'
      : 'audio/wav') as 'audio/wav' | 'audio/mpeg'

    onProgress?.('正在提交数字人生成任务...')

    const response = await fetch(GENERATE_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        imageBase64,
        imageMimeType,
        audioBase64,
        audioMimeType,
        resolution,
      }),
    })

    if (!response.ok) {
      let message = '数字人视频生成请求失败。'
      try {
        const payload = (await response.json()) as { message?: string; error?: string }
        message = payload.message || payload.error || message
      } catch {
        // ignore
      }
      throw new Error(message)
    }

    onProgress?.('正在生成数字人视频，预计需要 5-10 分钟...')
    const blob = await response.blob()
    if (blob.size === 0) {
      throw new Error('数字人视频生成没有返回视频数据。')
    }

    const mimeType = blob.type.startsWith('video/') ? blob.type : 'video/mp4'
    const file = new File([blob], createOutputFileName(), {
      type: mimeType,
      lastModified: Date.now(),
    })
    const objectUrl = URL.createObjectURL(blob)

    return { blob, file, objectUrl }
  }
}

export const aliyunDigitalHumanService = new AliyunDigitalHumanService()
